import React, { useState, useEffect, useReducer } from "react";
import openSocket from "../../services/socket-io";

import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  Chip,
  Tooltip,
} from "@material-ui/core";
import {
  Edit,
  DeleteOutline,
  Schedule,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import AutoReplyRuleModal from "../../components/AutoReplyRuleModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";

const reducer = (state, action) => {
  if (action.type === "LOAD_RULES") {
    const rules = action.payload;
    const newRules = [];

    rules.forEach((rule) => {
      const ruleIndex = state.findIndex((r) => r.id === rule.id);
      if (ruleIndex !== -1) {
        state[ruleIndex] = rule;
      } else {
        newRules.push(rule);
      }
    });

    return [...state, ...newRules];
  }

  if (action.type === "UPDATE_RULES") {
    const rule = action.payload;
    const ruleIndex = state.findIndex((r) => r.id === rule.id);

    if (ruleIndex !== -1) {
      state[ruleIndex] = rule;
      return [...state];
    } else {
      return [rule, ...state];
    }
  }

  if (action.type === "DELETE_RULES") {
    const ruleId = action.payload;

    const ruleIndex = state.findIndex((r) => r.id === ruleId);
    if (ruleIndex !== -1) {
      state.splice(ruleIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "SET_ORDER") {
    return action.payload;
  }
};

const DAY_LABELS = {
  0: i18n.t("autoReplyRuleModal.form.windows.days.sun"),
  1: i18n.t("autoReplyRuleModal.form.windows.days.mon"),
  2: i18n.t("autoReplyRuleModal.form.windows.days.tue"),
  3: i18n.t("autoReplyRuleModal.form.windows.days.wed"),
  4: i18n.t("autoReplyRuleModal.form.windows.days.thu"),
  5: i18n.t("autoReplyRuleModal.form.windows.days.fri"),
  6: i18n.t("autoReplyRuleModal.form.windows.days.sat"),
};

const describeWindow = (window) => {
  if (window.windowType === "specific_date") {
    return window.specificDate;
  }
  const days = (window.daysOfWeek || "")
    .split(",")
    .filter((d) => d.length > 0)
    .map((d) => DAY_LABELS[Number(d)])
    .join(", ");
  const offsetSuffix =
    window.endDayOffset > 0 ? ` (+${window.endDayOffset}d)` : "";
  return `${days} ${window.startTime}-${window.endTime}${offsetSuffix}`;
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const AutoReplyRules = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [rules, dispatch] = useReducer(reducer, []);
  const [selectedRule, setSelectedRule] = useState(null);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [deletingRule, setDeletingRule] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    const fetchRules = async () => {
      try {
        const { data } = await api.get("/autoReplyRules");
        dispatch({ type: "LOAD_RULES", payload: data });
        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    };
    dispatch({ type: "RESET" });
    fetchRules();
  }, []);

  useEffect(() => {
    const socket = openSocket();

    socket.on("autoReplyRule", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_RULES", payload: data.rule });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_RULES", payload: +data.ruleId });
      }

      if (data.action === "reorder") {
        dispatch({ type: "SET_ORDER", payload: data.rules });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleOpenRuleModal = () => {
    setSelectedRule(null);
    setRuleModalOpen(true);
  };

  const handleCloseRuleModal = () => {
    setSelectedRule(null);
    setRuleModalOpen(false);
  };

  const handleEditRule = (rule) => {
    setSelectedRule(rule.id);
    setRuleModalOpen(true);
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await api.delete(`/autoReplyRules/${ruleId}`);
      toast.success(i18n.t("autoReplyRules.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingRule(null);
  };

  const [reordering, setReordering] = useState(false);

  // Mueve una regla un puesto arriba/abajo en la lista y persiste el orden
  // completo como "priority" — la misma columna que ya decide cuál regla
  // gana cuando varias palabras clave coinciden, así que ordenar la lista
  // también resuelve choques entre reglas.
  const handleMoveRule = async (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= rules.length || reordering) return;

    const reordered = [...rules];
    [reordered[index], reordered[targetIndex]] = [
      reordered[targetIndex],
      reordered[index],
    ];

    dispatch({ type: "SET_ORDER", payload: reordered });
    setReordering(true);
    try {
      const { data } = await api.put("/autoReplyRules/reorder", {
        orderedIds: reordered.map((r) => r.id),
      });
      dispatch({ type: "SET_ORDER", payload: data });
    } catch (err) {
      toastError(err);
      dispatch({ type: "SET_ORDER", payload: rules });
    } finally {
      setReordering(false);
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      const { data } = await api.put(`/autoReplyRules/${rule.id}`, {
        active: !rule.active,
      });
      dispatch({ type: "UPDATE_RULES", payload: data });
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingRule &&
          `${i18n.t("autoReplyRules.confirmationModal.deleteTitle")} ${
            deletingRule.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteRule(deletingRule.id)}
      >
        {i18n.t("autoReplyRules.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <AutoReplyRuleModal
        open={ruleModalOpen}
        onClose={handleCloseRuleModal}
        ruleId={selectedRule}
      />
      <MainHeader>
        <Title>{i18n.t("autoReplyRules.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenRuleModal}
          >
            {i18n.t("autoReplyRules.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">
                {i18n.t("autoReplyRules.table.name")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("autoReplyRules.table.whatsapp")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("autoReplyRules.table.keywords")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("autoReplyRules.table.quickAnswer")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("autoReplyRules.table.active")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("autoReplyRules.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {rules.map((rule, index) => (
                <TableRow key={rule.id}>
                  <TableCell align="center">
                    {rule.name}
                    {rule.windows?.length > 0 && (
                      <Tooltip
                        title={rule.windows.map(describeWindow).join(" · ")}
                      >
                        <Schedule
                          fontSize="small"
                          color="primary"
                          style={{
                            verticalAlign: "middle",
                            marginLeft: 4,
                          }}
                        />
                      </Tooltip>
                    )}
                    {rule.parentRule && (
                      <div style={{ fontSize: 11, color: "#888" }}>
                        ↳ {i18n.t("autoReplyRules.table.dependsOn")}:{" "}
                        {rule.parentRule.name}
                      </div>
                    )}
                    {rule.fireAfterAssigned && (
                      <div style={{ fontSize: 11, color: "#888" }}>
                        ⚡ Dispara con agente ya asignado
                      </div>
                    )}
                    {rule.userScope === "some" && (
                      <div style={{ fontSize: 11, color: "#888" }}>
                        👤{" "}
                        {rule.users && rule.users.length > 0
                          ? rule.users.map((u) => u.name).join(", ")
                          : "ningún agente"}
                      </div>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {rule.whatsapp?.name}
                  </TableCell>
                  <TableCell align="center">
                    {rule.matchType === "any" ? (
                      <Chip
                        label={i18n.t(
                          "autoReplyRules.table.matchTypeAny"
                        )}
                        size="small"
                        color="primary"
                        style={{ margin: 2 }}
                      />
                    ) : (
                      (rule.keywords || "")
                        .split(",")
                        .filter((keyword) => keyword.trim().length > 0)
                        .map((keyword) => (
                          <Chip
                            key={keyword}
                            label={keyword.trim()}
                            size="small"
                            style={{ margin: 2 }}
                          />
                        ))
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {rule.quickAnswer?.shortcut}
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={rule.active}
                      onChange={() => handleToggleActive(rule)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      disabled={index === 0 || reordering}
                      onClick={() => handleMoveRule(index, -1)}
                    >
                      <KeyboardArrowUp />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={index === rules.length - 1 || reordering}
                      onClick={() => handleMoveRule(index, 1)}
                    >
                      <KeyboardArrowDown />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleEditRule(rule)}
                    >
                      <Edit />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => {
                        setConfirmModalOpen(true);
                        setDeletingRule(rule);
                      }}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={6} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default AutoReplyRules;
